import type { TripStatusType } from 'api/swagger';
import { formatDateTime } from 'shared/utils/time/format-date-time.ts';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { BaseAuditLogService } from './base-audit-log-service.ts';
import type { TAuditMessageSegment, IAuditLogService } from './types.ts';

export class TripAuditLogService extends BaseAuditLogService implements IAuditLogService {
  public override get messageSegments(): TAuditMessageSegment[] {
    switch (this.auditLog.action) {
      case 'cargo_linked':
        return this.cargoLinkedMessageSegments;
      case 'cargo_unlinked':
        return this.cargoUnlinkedMessageSegments;
      case 'status_changed':
        return this.statusChangedMessageSegments;
      case 'sync_to_fleethand':
        return this.syncToFleethandMessageSegments;
      default:
        return super.messageSegments;
    }
  }

  protected override formatValue(field: string, value: unknown): string {
    if (typeof value !== 'string' || value === '') return super.formatValue(field, value);

    switch (field) {
      case 'estimatedStartDate':
      case 'estimatedEndDate':
        return formatDate(value);
      case 'startDate':
      case 'endDate':
      case 'readyDate':
        return formatDateTime(value);
      default:
        return super.formatValue(field, value);
    }
  }

  private get statusChangedMessageSegments(): TAuditMessageSegment[] {
    const trip = this.primaryEntity;

    const tripSegments = this.isCurrentEntity(trip) ? [this.text('trip')] : [this.entity(trip)];
    const statusChange = this.getChange('status');

    const segments = [this.text(`${this.actor} changed the status of`), ...tripSegments];

    if (!statusChange) return segments;

    const oldStatus = statusChange.old as TripStatusType;
    const newStatus = statusChange.new as TripStatusType;

    return [
      ...segments,
      this.text('from'),
      { type: 'tripStatus', status: oldStatus },
      this.text('to'),
      { type: 'tripStatus', status: newStatus },
    ];
  }

  private get cargoLinkedMessageSegments(): TAuditMessageSegment[] {
    const segments = [this.text(`${this.actor} added`), ...this.cargoSegments, this.text('to'), ...this.tripSegments];

    if (this.cargoItemIds.length === 0) return segments;

    return [...segments, this.text('(partially in trip)')];
  }

  private get cargoUnlinkedMessageSegments(): TAuditMessageSegment[] {
    const isPartial = this.cargoItemIds.length > 0;
    const action = isPartial ? 'removed cargo item(s)' : 'removed';

    return [this.text(`${this.actor} ${action}`), ...this.cargoSegments, this.text('from'), ...this.tripSegments];
  }

  private get syncToFleethandMessageSegments(): TAuditMessageSegment[] {
    return [this.text(`${this.actor} synced tasks to Fleethand`)];
  }

  private get cargoSegments(): TAuditMessageSegment[] {
    const cargo = this.relatedEntity;

    return cargo ? [this.entity(cargo)] : [this.text('cargo')];
  }

  private get tripSegments(): TAuditMessageSegment[] {
    const trip = this.primaryEntity;

    return this.isCurrentEntity(trip) ? [this.text('the trip')] : [this.entity(trip)];
  }

  private get cargoItemIds(): number[] {
    return (this.auditLog.metadata?.cargoItemIds as number[] | undefined) ?? [];
  }
}
